import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Clock, CheckCircle, XCircle, Loader2, Trash2, RotateCcw,
  Eye, Send, ThumbsUp, Play, Calendar, AlertTriangle
} from "lucide-react";

const CMD_ICONS = {
  fetch_views: Eye,
  fetch_stats: Play,
  post_comment: Send,
  upvote: ThumbsUp,
  detect_accounts: RotateCcw,
};

const CMD_COLORS = {
  fetch_views: "#60a5fa",
  fetch_stats: "#60a5fa",
  post_comment: "#a78bfa",
  upvote: "#f97316",
  detect_accounts: "#6b7280",
};

const STATUS_COLORS = {
  scheduled: "#f59e0b",
  pending: "#3b82f6",
  in_progress: "#3b82f6",
  completed: "#10b981",
  failed: "#ef4444",
};

const DRIP_LABELS = {
  instant: "Instant",
  "1hour": "1 Hour",
  "1day": "1 Day",
  "1week": "1 Week",
};

function formatTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const now = new Date();
  const diff = d - now;
  if (diff > 0) {
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `in ${hrs}h ${mins % 60}m`;
    const days = Math.floor(hrs / 24);
    return `in ${days}d ${hrs % 24}h`;
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ScheduleTab() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await api.get("/bridge/schedule");
      setData(res.data);
    } catch (err) {
      console.error("Schedule fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSchedule();
    const interval = setInterval(fetchSchedule, 10000);
    return () => clearInterval(interval);
  }, [fetchSchedule]);

  const handleCancel = async (cmdId) => {
    setCancelling(cmdId);
    try {
      await api.delete(`/bridge/schedule/${cmdId}`);
      fetchSchedule();
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setCancelling(null);
    }
  };

  const handleClearAll = async () => {
    try {
      await api.delete("/bridge/commands/clear");
      fetchSchedule();
    } catch (err) {
      console.error("Clear all failed:", err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "#6b7280" }}>
        <Loader2 size={20} className="animate-spin" style={{ margin: "0 auto 8px", display: "block" }} />
        Loading schedule...
      </div>
    );
  }

  if (!data) return null;

  const { summary, scheduled, pending, recent } = data;

  const renderCommand = (cmd, showCancel = false) => {
    const Icon = CMD_ICONS[cmd.command_type] || Clock;
    const color = CMD_COLORS[cmd.command_type] || "#6b7280";

    return (
      <div key={cmd.id} style={{
        display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
        background: "#111113", borderRadius: 6, fontSize: 12, marginBottom: 4,
      }}>
        <Icon size={14} color={color} style={{ flexShrink: 0 }} />
        <span style={{ color: "#e5e5e5", fontWeight: 500, minWidth: 85 }}>
          {cmd.command_type === "fetch_views" ? "Fetch Views" :
           cmd.command_type === "post_comment" ? "Comment" :
           cmd.command_type === "upvote" ? "Upvote" :
           cmd.command_type === "fetch_stats" ? "Stats" :
           cmd.command_type}
        </span>
        <span style={{ color: "#6b7280", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {cmd.account || "-"} · {cmd.url ? cmd.url.substring(0, 45) + "..." : ""}
        </span>
        {cmd.drip_feed && cmd.drip_feed !== "instant" && (
          <span style={{ fontSize: 10, color: "#f59e0b", background: "#2d1f0e", padding: "1px 6px", borderRadius: 4, flexShrink: 0 }}>
            {DRIP_LABELS[cmd.drip_feed] || cmd.drip_feed}
          </span>
        )}
        <span style={{ fontSize: 11, color: STATUS_COLORS[cmd.status] || "#6b7280", flexShrink: 0, minWidth: 55 }}>
          {cmd.status}
        </span>
        <span style={{ fontSize: 10, color: "#4b5563", flexShrink: 0, minWidth: 55, textAlign: "right" }}>
          {formatTime(cmd.scheduled_at)}
        </span>
        {cmd.result?.views != null && (
          <span style={{ fontFamily: "JetBrains Mono", fontSize: 10, color: "#60a5fa", flexShrink: 0 }}>
            {cmd.result.views}v
          </span>
        )}
        {cmd.error && (
          <span style={{ fontSize: 10, color: "#ef4444", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }} title={cmd.error}>
            {cmd.error.substring(0, 30)}
          </span>
        )}
        {showCancel && (
          <button
            onClick={() => handleCancel(cmd.id)}
            disabled={cancelling === cmd.id}
            style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", padding: 2, flexShrink: 0 }}
            data-testid={`cancel-cmd-${cmd.id}`}
          >
            {cancelling === cmd.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "16px 0" }} data-testid="schedule-tab">
      {/* Summary Cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Scheduled</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#f59e0b", fontFamily: "JetBrains Mono" }}>
            {summary.scheduled}
          </div>
        </div>
        <div style={{ flex: 1, background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Pending / Active</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#3b82f6", fontFamily: "JetBrains Mono" }}>
            {summary.pending}
          </div>
        </div>
        <div style={{ flex: 1, background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>Next Run</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#e5e5e5" }}>
            {summary.next_at ? formatTime(summary.next_at) : "—"}
          </div>
        </div>
      </div>

      {/* Actions */}
      {(summary.scheduled > 0 || summary.pending > 0) && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
          <button onClick={handleClearAll} className="btn btn-sm" style={{ fontSize: 11, color: "#ef4444", borderColor: "#ef444433" }} data-testid="clear-all-schedule">
            <Trash2 size={12} /> Cancel All ({summary.scheduled + summary.pending})
          </button>
        </div>
      )}

      {/* Scheduled Commands */}
      {scheduled.length > 0 && (
        <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#f59e0b", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar size={14} /> Scheduled ({scheduled.length})
          </div>
          <div style={{ maxHeight: 250, overflowY: "auto" }}>
            {scheduled.map(cmd => renderCommand(cmd, true))}
          </div>
        </div>
      )}

      {/* Pending/Active */}
      {pending.length > 0 && (
        <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#3b82f6", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Loader2 size={14} /> Active / Pending ({pending.length})
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {pending.map(cmd => renderCommand(cmd, true))}
          </div>
        </div>
      )}

      {/* Recent History */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#e5e5e5", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
          <Clock size={14} /> Recent History ({recent.length})
        </div>
        {recent.length === 0 ? (
          <div style={{ fontSize: 12, color: "#4b5563", textAlign: "center", padding: 20 }}>
            No completed commands yet.
          </div>
        ) : (
          <div style={{ maxHeight: 250, overflowY: "auto" }}>
            {recent.map(cmd => renderCommand(cmd))}
          </div>
        )}
      </div>
    </div>
  );
}
